import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { NotificationService } from '../../service/notification.service';
import { NotificationType } from '../../enum/notification-type.enum';
import { ReviewCommissionsService } from 'src/app/service/commission-summary/review-commissions.service';
import { ReviewCommissionsModel } from 'src/app/models/commission-summary/reviewcommissions.model';
import { GlobalService } from 'src/app/service/global.service';
import { HomeService } from 'src/app/service/home.service';

@Component({
  selector: 'app-commissions-summary-search',
  templateUrl: './commissions-summary-search.component.html',
  styleUrls: ['./commissions-summary-search.component.css']
})
export class CommissionsSummarySearchComponent implements OnInit {

  reviewCommissionsModel = new ReviewCommissionsModel();

  searchDataList: ReviewCommissionsModel[] = [];
  salesChannelsList: ReviewCommissionsModel[] = [];
  commissionPlansList: ReviewCommissionsModel[] = [];
  filteredCommissionPlansList: ReviewCommissionsModel[] = [];
  payrollEndDatesList: ReviewCommissionsModel[] = [];
  searchCriteriaSet = new Set<string>();
  getValidDates: ReviewCommissionsModel[] = [];
  effectiveDate: any;
  validToDt: Date;
  validFromDt: Date;
  isFieldsdDisabled: boolean = true;
  isAdminFieldsdDisabled: boolean = true;

  constructor(
    private router: Router,
    private reviewCommissionsService: ReviewCommissionsService,
    private homeService: HomeService,
    private notificationService: NotificationService,
    private globalService: GlobalService
  ) {
    globalService._disableBackButton();
  }

  ngOnInit() {
    const role = localStorage.getItem('CURRENT_USERROLE_SESSION');
    if (role === 'Sales Representative') {
      this.getRepSupSearchData();
      this.isFieldsdDisabled = false;
      this.isAdminFieldsdDisabled = true;
    } else if (role === 'Supervisor' || role === 'Sales Channel Admin') {
      this.getRepSupSearchData();
      this.isFieldsdDisabled = false;
      this.isAdminFieldsdDisabled = true;
    } else {
      this.getSearchData();
      this.isFieldsdDisabled = true;
      this.isAdminFieldsdDisabled = false;
    }
  }

  getSearchData() {
    this.reviewCommissionsService.getSearchData().subscribe(
      (response: ReviewCommissionsModel[]) => {
        this.searchDataList = response;
        this.reviewCommissionsService.setSearchDropdownCache(response);
        this.getSalesChannels();
      },
      (errorResponse: HttpErrorResponse) => {
        this.notificationService.sendNotification(
          NotificationType.ERROR,
          ReviewCommissionsService.extractHttpErrorMessage(errorResponse)
        );
      }
    );
  }

  getRepSupSearchData() {
    this.reviewCommissionsService.getRepSupSearchData(localStorage.getItem('CURRENT_EMPLOYEEID_SESSION')).subscribe(
      (response: ReviewCommissionsModel[]) => {
        this.searchDataList = response;
        this.reviewCommissionsService.setSearchDropdownCache(response);
        this.getSalesChannels();
      },
      (errorResponse: HttpErrorResponse) => {
        this.notificationService.sendNotification(
          NotificationType.ERROR,
          ReviewCommissionsService.extractHttpErrorMessage(errorResponse)
        );
      }
    );
  }

  getSalesChannels() {
    this.salesChannelsList = this.uniqueBy(
      this.searchDataList.filter(item => !!item.salesChannel),
      item => (item.salesChannel)
    ).sort((a, b) => (a.salesChannel < b.salesChannel) ? -1 : 1);
    this.getCommissionPlans(localStorage.getItem('SESSION_SC'));
  }

  getCommissionPlans(filterValue: string) {
    this.commissionPlansList = this.uniqueBy(
      this.searchDataList.filter(item => item.salesChannel === filterValue && !!item.commPlan && item.isSalesRepAccess =='Y'),
      item => item.commPlan
    ).sort((a, b) => (a.commPlan < b.commPlan) ? -1 : 1);
    this.reconcileSelectedCommissionPlans();
    this.getPayrollEndDates();
  }

  public getPayrollEndDates() {
    this.payrollEndDatesList = this.uniqueBy(
      this.searchDataList.filter(item => item.validToDt != null),
      item => String(item.validToDt)
    );
    this.payrollEndDatesList = this.payrollEndDatesList.filter(item =>
      this.isPayrollEndDateWithinCurrentMonthOrEarlier(item.validToDt)
    );
    this.payrollEndDatesList = this.payrollEndDatesList.sort((a, b) => (a.validToDt > b.validToDt) ? -1 : 1);
    this.loadDefaults();
  }

  public loadDefaults() {
    if (!localStorage.getItem('SESSION_SC')) {
      return;
    }

    this.reviewCommissionsModel.salesChannel = localStorage.getItem('SESSION_SC');
    this.reviewCommissionsModel.calRunId = localStorage.getItem('SESSION_CALRUNID');

    const role = localStorage.getItem('CURRENT_USERROLE_SESSION');
    if (!ReviewCommissionsService.isAdminRole(role)) {
      this.reviewCommissionsModel.firstName = localStorage.getItem('SESSION_FNAME');
      this.reviewCommissionsModel.lastName = localStorage.getItem('SESSION_LNAME');
      this.reviewCommissionsModel.employeeId = localStorage.getItem('SESSION_EMPID');
      this.reviewCommissionsModel.scEmpID = localStorage.getItem('SESSION_COMPASID');
      this.reviewCommissionsModel.sales_rep_id = localStorage.getItem('SESSION_REPID');
    }

    const storedCommPlan = localStorage.getItem('SESSION_COMMPLAN');
    if (storedCommPlan) {
      try {
        this.reviewCommissionsModel.commissionPlanId = JSON.parse(storedCommPlan);
      } catch {
        this.reviewCommissionsModel.commissionPlanId = null;
      }
    }
    this.sanitizeCommissionPlanSelection();

    if (this.reviewCommissionsModel.calRunId) {
      const calRunStillVisible = this.payrollEndDatesList.some(
        item => String(item.calRunId) === String(this.reviewCommissionsModel.calRunId)
      );
      if (!calRunStillVisible) {
        this.reviewCommissionsModel.calRunId = null;
      } else {
        this.syncPayrollCalendarDates(this.reviewCommissionsModel.calRunId, this.reviewCommissionsModel.commissionPlanId);
      }
    }
  }

  public getSelectedSalesChannel(event: any) {
    const filterValue = event.target.value;
    this.commissionPlansList = this.uniqueBy(
      this.searchDataList.filter(item => item.salesChannel === filterValue && !!item.commPlan),
      item => item.commPlan
    ).sort((a, b) => (a.commPlan < b.commPlan) ? -1 : 1);
    this.reconcileSelectedCommissionPlans();
  }

  public getSelectedPayrollCalendar(event: any) {
    const filterValue = this._normalizeValue(event.target.value);
    this.getValidDates = this.payrollEndDatesList.filter(
      item => this._normalizeValue(String(item.calRunId)) === filterValue
    );

    if (!this.getValidDates.length) {
      return;
    }

    this.validFromDt = this.getValidDates[0].validFromDt;
    this.validToDt = this.getValidDates[0].validToDt;
    this.persistPayrollCalendarDates(this.getValidDates[0].validFromDt, this.getValidDates[0].validToDt);
  }

  public getResultsByKpi(url: string, pageName: string, pageType: string) {
    this.sanitizeCommissionPlanSelection();

    if (!this.hasMeaningfulValue(this.reviewCommissionsModel.calRunId)) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please select Payroll End Date');
      return;
    }

    const m = this.reviewCommissionsModel;
    const atLeastOne =
      this.hasMeaningfulValue(m.salesChannel) ||
      this.hasMeaningfulValue(m.commissionPlanId) ||
      this.hasMeaningfulValue(m.firstName) ||
      this.hasMeaningfulValue(m.lastName) ||
      this.hasMeaningfulValue(m.scEmpID) ||
      this.hasMeaningfulValue(m.employeeId) ||
      this.hasMeaningfulValue(m.sales_rep_id);
    if (!atLeastOne) {
      this.notificationService.sendNotification(
        NotificationType.ERROR,
        'Please enter at least one of: Sales Channel, Commission Plan, First Name, Last Name, Compas ID, Employee ID, or Sales Rep ID'
      );
      return;
    }

    this.syncPayrollCalendarDates(this.reviewCommissionsModel.calRunId, this.reviewCommissionsModel.commissionPlanId);
    this.reviewCommissionsModel.teamDataCheckedactive = false;
    // currency formatting can key off country instead of sales channel (HOA-8305).
    const selectedPlanId = this.reviewCommissionsModel.commissionPlanId?.[0];
    if (selectedPlanId != null) {
      const selectedRow = this.searchDataList.find(r => String(r.commPlanId) === String(selectedPlanId));
      this.reviewCommissionsModel.country = selectedRow?.country ?? this.reviewCommissionsModel.country ?? '';
    }
    this.persistEffectiveDate();

    ReviewCommissionsService.applyRoleBasedSearchScope(this.reviewCommissionsModel);

    const headerInfo = this.buildSelectedCommPlanHeader(pageType);
    this.getDisputeStatus1(() => this.navigateToReviewCommissionsNew(url, pageName, headerInfo));
  }

  private buildSelectedCommPlanHeader(pageType: string): string {
    if (!this.reviewCommissionsModel.commissionPlanId?.length) {
      return pageType;
    }

    let selectedCommPlanHeader = '';
    for (const val of this.reviewCommissionsModel.commissionPlanId) {
      this.filteredCommissionPlansList = this.commissionPlansList.filter(item => item.commPlanId == val);
      for (const comm of this.filteredCommissionPlansList) {
        selectedCommPlanHeader = selectedCommPlanHeader + comm.commPlan + ',';
      }
    }
    return pageType + ' - ' + selectedCommPlanHeader.slice(0, -1);
  }

  private getDisputeStatus1(onComplete: () => void): void {
    const commPlanId = this.reviewCommissionsModel.commissionPlanId?.[0];
    const calRunId = Number(this.reviewCommissionsModel.calRunId);

    if (!commPlanId || !calRunId) {
      ReviewCommissionsService.persistDisputeCloseStatus([]);
      onComplete();
      return;
    }

    this.homeService.getDisputeStatus(commPlanId, calRunId).subscribe(
      (data: any) => {
        ReviewCommissionsService.persistDisputeCloseStatus(data);
        onComplete();
      },
      (errorResponse: HttpErrorResponse) => {
        this.notificationService.sendNotification(
          NotificationType.ERROR,
          ReviewCommissionsService.extractHttpErrorMessage(errorResponse)
        );
      }
    );
  }

  private navigateToReviewCommissionsNew(url: string, pageName: string, headerInfo: string): void {
    this.reviewCommissionsService.prepareNewSearchSession(
      this.reviewCommissionsModel,
      pageName,
      headerInfo,
      this.searchCriteriaSet
    );
    this.router.navigate(['/' + url], { queryParams: { page: pageName } });
  }

  public clearSearchCriteria() {
    this.reviewCommissionsService.clearSessionStorage();
    this.reviewCommissionsModel.salesChannel = null;
    this.reviewCommissionsModel.commissionPlanId = null;
    this.reviewCommissionsModel.calRunId = null;
    this.reviewCommissionsModel.firstName = null;
    this.reviewCommissionsModel.lastName = null;
    this.reviewCommissionsModel.employeeId = null;
    this.reviewCommissionsModel.scEmpID = null;
    this.reviewCommissionsModel.sales_rep_id = null;
  }

  private sanitizeCommissionPlanSelection(): void {
    const rawSelection = this.reviewCommissionsModel.commissionPlanId as any;
    if (rawSelection == null || rawSelection === '') {
      this.reviewCommissionsModel.commissionPlanId = null;
      return;
    }

    const values = Array.isArray(rawSelection) ? rawSelection : [rawSelection];
    const cleaned = values
      .map((value: any) => Number(value))
      .filter((value: number) => Number.isInteger(value) && value > 0);

    this.reviewCommissionsModel.commissionPlanId = cleaned.length ? cleaned : null;
  }

  private hasMeaningfulValue(value: any): boolean {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (value == null) {
      return false;
    }
    const normalized = String(value).trim().toLowerCase();
    if (!normalized || normalized === 'null' || normalized.startsWith('--select')) {
      return false;
    }
    return true;
  }

  private reconcileSelectedCommissionPlans(): void {
    this.sanitizeCommissionPlanSelection();
    const selected = this.reviewCommissionsModel.commissionPlanId;
    if (!selected?.length) {
      return;
    }

    const availableIds = new Set(this.commissionPlansList.map(item => Number(item.commPlanId)));
    const filteredSelected = selected.filter(id => availableIds.has(Number(id)));
    this.reviewCommissionsModel.commissionPlanId = filteredSelected.length ? filteredSelected : null;
  }

  private persistEffectiveDate(): void {
    const calRunId = this.reviewCommissionsModel.calRunId;
    const planIds = this.reviewCommissionsModel.commissionPlanId;
    let match = this.searchDataList.find(item => String(item.calRunId) === String(calRunId));

    if (planIds?.length === 1) {
      const byPlan = this.searchDataList.find(
        item => String(item.calRunId) === String(calRunId) && item.commPlanId === planIds[0]
      );
      if (byPlan) {
        match = byPlan;
      }
    }

    if (match?.effectiveDate) {
      this.effectiveDate = match.effectiveDate;
      localStorage.setItem('SESSION_EFFDATE', String(match.effectiveDate));
    }
  }

  private syncPayrollCalendarDates(calRunId: any, commissionPlanIds: number[] | null): void {
    const inMemoryFrom = ReviewCommissionsService.toDate(this.validFromDt);
    const inMemoryTo = ReviewCommissionsService.toDate(this.validToDt);
    if (ReviewCommissionsService.isValidPayrollDateRange(inMemoryFrom, inMemoryTo)) {
      this.reviewCommissionsModel.validFromDt = inMemoryFrom!;
      this.reviewCommissionsModel.validToDt = inMemoryTo!;
      this.persistPayrollCalendarDates(inMemoryFrom, inMemoryTo);
      return;
    }

    const planIds = commissionPlanIds ?? [];
    let match = planIds.length === 1
      ? this.searchDataList.find(
        item => String(item.calRunId) === String(calRunId) && Number(item.commPlanId) === Number(planIds[0])
      )
      : undefined;
    if (!match) {
      match = this.searchDataList.find(item => String(item.calRunId) === String(calRunId));
    }

    if (match) {
      const validFromDt = ReviewCommissionsService.toDate(match.validFromDt);
      const validToDt = ReviewCommissionsService.toDate(match.validToDt);
      if (ReviewCommissionsService.isValidPayrollDateRange(validFromDt, validToDt)) {
        this.reviewCommissionsModel.validFromDt = validFromDt!;
        this.reviewCommissionsModel.validToDt = validToDt!;
        this.validFromDt = validFromDt!;
        this.validToDt = validToDt!;
        this.persistPayrollCalendarDates(validFromDt, validToDt);
        return;
      }
    }

    const storedFrom = ReviewCommissionsService.toDate(localStorage.getItem('validFromDt'));
    const storedTo = ReviewCommissionsService.toDate(localStorage.getItem('validToDt'));
    if (ReviewCommissionsService.isValidPayrollDateRange(storedFrom, storedTo)) {
      this.reviewCommissionsModel.validFromDt = storedFrom!;
      this.reviewCommissionsModel.validToDt = storedTo!;
      return;
    }

    this.reviewCommissionsModel.validFromDt = storedFrom ?? inMemoryFrom ?? undefined;
    this.reviewCommissionsModel.validToDt = storedTo ?? inMemoryTo ?? undefined;
  }

  private persistPayrollCalendarDates(validFromDt: unknown, validToDt: unknown): void {
    const fromValue = ReviewCommissionsService.toStoredDateValue(validFromDt);
    const toValue = ReviewCommissionsService.toStoredDateValue(validToDt);
    if (fromValue) {
      localStorage.setItem('validFromDt', fromValue);
    }
    if (toValue) {
      localStorage.setItem('validToDt', toValue);
    }
  }

  private isPayrollEndDateWithinCurrentMonthOrEarlier(validToDt: any): boolean {
    if (!validToDt) {
      return false;
    }

    const payrollEndDate = new Date(validToDt);
    if (isNaN(payrollEndDate.getTime())) {
      return false;
    }

    const now = new Date();
    const payrollMonthKey = payrollEndDate.getFullYear() * 12 + payrollEndDate.getMonth();
    const currentMonthKey = now.getFullYear() * 12 + now.getMonth();
    return payrollMonthKey <= currentMonthKey;
  }

  private _normalizeValue(value: string): string {
    if (value) {
      return value.toString().toLowerCase().replace(/\s/g, '');
    }
    return '';
  }

  private uniqueBy<T>(items: T[], keyFn: (item: T) => string): T[] {
    const seen = new Set<string>();
    const result: T[] = [];
    for (const item of items) {
      const rawKey = keyFn(item);
      if (!rawKey) {
        continue;
      }
      const key = rawKey.trim();
      if (!key || seen.has(key)) {
        continue;
      }
      seen.add(key);
      result.push(item);
    }
    return result;
  }
}
